import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar } from "@/components/ProfileIdentityCard";
import { Shield, LogOut } from "lucide-react";
import MobileTabBar from "@/components/MobileTabBar";

export default function AppHeader() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const navItems = user && user.paid ? [
    { to: "/dashboard", label: "Vaults" },
    { to: "/banking", label: "Banking" },
    { to: "/map", label: "Map" },
    { to: "/level-up", label: "Level Up" },
    { to: "/budget", label: "Budget" },
    { to: "/baby-funds", label: "Baby Funds" },
    { to: "/inventory", label: "Inventory" },
    { to: "/networth", label: "Net Worth" },
    { to: "/beneficiaries", label: "Beneficiaries" },
    { to: "/activity", label: "Activity" },
    { to: "/support", label: "Support" },
    ...(user.is_admin ? [{ to: "/admin", label: "Admin" }] : []),
  ] : [];

  const isActive = (to) => loc.pathname === to || (to !== "/dashboard" && loc.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-cream/85 border-b border-sand" data-testid="app-header">
      {user?.demo && (
        <div className="bg-gold-dark text-white text-[11px] uppercase tracking-[0.2em] font-semibold text-center py-1.5 px-4"
             data-testid="demo-banner">
          <span className="sm:hidden">Demo mode · play money only</span>
          <span className="hidden sm:inline">Demo mode · play money only · preorder to become a Founding Member</span>
        </div>
      )}
      {/* Row 1: brand + identity controls */}
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 h-14 sm:h-16 flex items-center justify-between gap-3">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 shrink-0" data-testid="brand-link">
          <Shield className="w-5 h-5 text-forest" strokeWidth={1.5} />
          <span className="font-serif text-lg sm:text-xl tracking-tight text-ink">Withdraw Guard</span>
        </Link>

        <div className="flex items-center gap-3 sm:gap-4 shrink-0">
          {user ? (
            <>
              <Link to="/profile"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-sage hover:text-ink transition-colors max-w-[240px]"
                data-testid="user-email">
                <Avatar user={user} size={28} />
                <span className="hidden md:inline truncate">{user.username ? `@${user.username}` : user.email}</span>
              </Link>
              <button onClick={() => { logout(); nav("/"); }}
                className="inline-flex items-center gap-1.5 text-sm text-moss hover:text-clay transition-colors"
                data-testid="logout-btn">
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm text-moss hover:text-ink" data-testid="header-login-link">Sign in</Link>
              <Link to="/register" className="bg-forest text-white rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium hover:bg-forest-deep transition-colors" data-testid="header-cta">
                Open account
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Row 2: nav strip — desktop/tablet. On phones the bottom tab bar takes over. */}
      {navItems.length > 0 && (
        <div className="hidden sm:block border-t border-sand/60 bg-cream/60" data-testid="primary-nav-wrap">
          <nav className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-10" data-testid="primary-nav">
            <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto no-scrollbar py-1.5 sm:py-2">
              {navItems.map((n) => {
                const active = isActive(n.to);
                return (
                  <Link key={n.to} to={n.to}
                    className={`shrink-0 text-xs sm:text-sm font-sans tracking-wide px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-colors ${
                      active
                        ? "bg-forest text-white"
                        : "text-moss hover:text-ink hover:bg-linen"
                    }`}
                    data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    {n.label}
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>
      )}
      {navItems.length > 0 && <MobileTabBar items={navItems} />}
    </header>
  );
}
